import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";

interface AddStaffFormProps {
  onClose: () => void;
  onSubmit: () => void;
  isStaffModalOpen: boolean;
}

interface StaffFormValues {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  description: string;
  status: string;
}

const StaffAdd: React.FC<AddStaffFormProps> = ({
  onClose,
  onSubmit,
  isStaffModalOpen,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be at most 50 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be at most 50 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    contactNumber: Yup.string()
      .required("Contact number is required")
      .matches(/^\d{10,15}$/, "Contact number must be between 10-15 digits"),
  });

  const handleSubmit = async (values: StaffFormValues) => {
    try {
      setIsLoading(true);
      const { unAuthenticatedCall } = actionsHandler("staffAdd");

      const response = await dispatch(
        unAuthenticatedCall({
          payload: {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            phone: values.contactNumber,
          },
          method: "post",
        }),
      );

      if (response.ok) {
        toast.success("Staff member added successfully");
        onSubmit();
      } else {
        if (Array.isArray(response.data.message)) {
          response.data.message.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error(response.data.message || "Failed to add staff member");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to add staff member. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      description: "",
      status: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-z-50 tw-transition-all tw-ease-in-out tw-duration-1000 ${
        isStaffModalOpen ? "tw-right-0" : "tw-right-[-265px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-screen tw-w-[350px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
          <h2 className="tw-text-base tw-font-semibold">Add Task</h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        <div className="tw-flex tw-flex-col tw-justify-between tw-h-[95%] tw-px-4 tw-py-4">
          <form onSubmit={formik.handleSubmit} className="tw-space-y-6">
            {/* First Name Input */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Task Title *
              </label>
              <input
                type="text"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter task title "
                className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                  formik.touched.firstName && formik.errors.firstName
                    ? "tw-border-red-500"
                    : "tw-border-[#D5D7DA]"
                } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
              />
              {formik.touched.firstName && formik.errors.firstName && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                  {formik.errors.firstName}
                </div>
              )}
            </div>

            {/* Last Name Input */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Start Date *
              </label>
              <input
                type="text"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Select start date"
                className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                  formik.touched.lastName && formik.errors.lastName
                    ? "tw-border-red-500"
                    : "tw-border-[#D5D7DA]"
                } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
              />
              {formik.touched.lastName && formik.errors.lastName && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                  {formik.errors.lastName}
                </div>
              )}
            </div>

            {/* Email Input */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                End Date
              </label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Select end date"
                className={`tw-w-full tw-h-11 tw-px-3 tw-border ${
                  formik.touched.email && formik.errors.email
                    ? "tw-border-red-500"
                    : "tw-border-[#D5D7DA]"
                } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                  {formik.errors.email}
                </div>
              )}
            </div>

            {/* Contact Number Input */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                End Date
              </label>
              <textarea
                name="description"
                id="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Write here..."
                className={`tw-w-full tw-h-[500px] tw-px-3 tw-border tw-py-3 ${
                  formik.touched.email && formik.errors.email
                    ? "tw-border-red-500"
                    : "tw-border-[#D5D7DA]"
                } tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500`}
              ></textarea>
            </div>
            <li
              className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-sm tw-w-fit ${
                formik.values.status === "Pending"
                  ? "tw-bg-[#ECFDF3] tw-text-[#027A48]"
                  : "tw-bg-[#F5F5F5] tw-text-[#414651]"
              }`}
            >
              <span
                className={`tw-w-2 tw-h-2 tw-rounded-full ${
                  formik.values.status === "Pending"
                    ? "tw-bg-[#12B76A]"
                    : "tw-bg-[#717680]"
                }`}
              ></span>
              Pending
            </li>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !formik.isValid}
              className="tw-w-full tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#146C94] focus:tw-ring-offset-2 tw-transition-colors disabled:tw-opacity-50"
            >
              {isLoading ? "Adding..." : "Add Task"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StaffAdd;
